import React, { forwardRef } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Box, LazyImage } from "../../styled/Elements";
import {
  InvoiceTxtAr,
  InvoiceTxtEn,
  Location,
  Mail,
  PhoneIcon,
  PhoneIconWhite,
  RiyalIconSvg,
  RiyalSymbol,
} from "../../styled/AllImages";
import { formateAmounts } from "../../../utils";
import { QRCodeCanvas } from "qrcode.react";

/* === STYLES === */
/* === STYLES === */
const TopBorder = styled.div`
  display: flex;
  width: 100%;
  height: 10px; /* adjust height as needed */

  .left {
    flex: 1;
    background: #466eeb; /* light blue */
  }
  .right {
    flex: 3;
    background: #2b318a; /* dark blue */
  }
`;

const PdfPage = styled.div`
  /* display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 1050px; */
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 1100px;
  position: relative;
  max-width: 800px;
  margin: 0 auto;

  /* padding-bottom: 120px; */
  p {
    color: #333 !important;
    margin: 0;
    padding: 0;
  }

  h5 {
    color: #9a9aa7;
  }

  .font-bold {
    font-weight: bolder;
  }

  .username {
    font-weight: bolder;
    padding-bottom: 0;
    margin-bottom: 0;
  }

  .totals-content {
    padding: 0.6rem 0 1rem 0;
    border-bottom: 1px solid #e4e8ef;
  }

  .label {
    color: #9a9aa7 !important;
  }
  .totals-section {
    padding: 0 20px;
  }

  table,
  tr,
  td,
  th {
    page-break-inside: avoid !important;
  }

  .no-break {
    page-break-inside: avoid !important;
  }

  .footer {
    position: relative;
    bottom: 0;
    width: 100%;
  }
`;

const PdfWrapper = styled.div`
  flex: 1;
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
  background: #fff;
  padding: 30px 20px;
  color: #333;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e4e8ef;
  .user-head {
    text-align: end;
    font-weight: bolder;
  }
  .header-bg {
    flex: 1;
    display: flex;
    justify-content: center;
    position: relative;
  }
  .header-bg img {
    /* width: 248px; */
  }
`;

const Logo = styled.div``;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 25px 0;

  thead {
    background: #1f3c88;
    color: #fff;
  }

  th,
  td {
    border-bottom: 1px solid #ddd;
    padding: 14px 10px;
    font-size: 14px;
    text-align: center;
  }

  th {
    font-weight: 600;
  }

  tbody td {
    background-color: #f8faff;
  }
`;

const Totals = styled.div`
  min-width: 350px;
  max-width: 600px;
  margin-top: 20px;

  p {
    display: flex;
    justify-content: space-between;
    margin: 5px 0;
  }

  h3,
  h4,
  h5 {
    font-family: var(--black);
    font-weight: 900;
  }

  .grand-total {
    background: #1f3c88;
    color: #fff;
    font-weight: bolder;
    font-size: 18px;
    padding: 1.2rem 12px;
    display: flex;
    justify-content: space-between;
    margin-top: 15px;
  }
`;

const Note = styled.div`
  margin-top: 25px;
  color: #555;
`;

const Footer = styled.div`
  background: #466eeb;
  padding: 15px;
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: #fff;

  margin-top: auto; /* pushes footer down */
  break-inside: avoid;
  page-break-inside: avoid;
`;

/* === COMPONENT === */
const QRInvoicePDF = forwardRef(({ userData, invoiceData, logo }, ref) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  // const qrValue = `
  //     Seller: ${userData?.organization?.name}
  //     VAT: ${userData?.organization?.vat_number}
  //     Total: ${invoiceData?.total_amount}
  //     Tax: ${invoiceData?.tax_value}
  //     Date: ${invoiceData?.date}
  //     `;

  if (!invoiceData) return <div>Invoice Record not found!</div>;

  return (
    <PdfPage>
      <Box>
        <TopBorder>
          <div className="left" />
          <div className="right" />
        </TopBorder>
        <PdfWrapper ref={ref} dir={isRTL ? "rtl" : "ltr"}>
          {/* HEADER */}
          <Header>
            <Logo>{logo && <img src={logo} alt="logo" width={134} />}</Logo>

            <div className="header-bg mt-5">
              <div>
                <img
                  src={isRTL ? InvoiceTxtAr : InvoiceTxtEn}
                  alt="Header Background"
                  crossOrigin="anonymous"
                />
              </div>
            </div>
            <div>
              <h4 className="user-head m-0 p-0">{invoiceData?.name}</h4>
              <Box className="d-flex flex-column align-items-end gap-1 mt-2">
                <p className="d-flex align-items-center gap-4">
                  <span className="label">{t("invoice_no")} </span>
                  {invoiceData?.invoice_no}
                </p>
                <p className="d-flex align-items-center gap-4">
                  <span className="label">{t("issue_date")} </span>
                  {invoiceData?.date}
                </p>
              </Box>
            </div>
          </Header>

          {/* FROM / TO */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "20px",
            }}
          >
            <div>
              <span className="label">{t("invoice_from")}</span>
              <p className="username">{userData?.organization?.name}</p>
              {userData?.organization?.contact_phone && (
                <p>
                  {t("mobile_no")}{" "}
                  <span style={{ direction: "ltr", unicodeBidi: "embed" }}>
                    {userData?.organization?.contact_phone}
                  </span>
                </p>
              )}
              {userData?.organization?.vat_number && (
                <p>
                  {t("tax_number")} {userData?.organization?.vat_number}
                </p>
              )}
            </div>
            <div>
              <span className="label">{t("invoice_to")}</span>
              <p className="username">
                {isRTL
                  ? invoiceData?.client_name_ar
                  : invoiceData?.client_name_en}
              </p>
              {invoiceData?.phone && (
                <p>
                  {t("mobile_no")}{" "}
                  <span style={{ direction: "ltr", unicodeBidi: "embed" }}>
                    {invoiceData?.phone}
                  </span>
                </p>
              )}
              {invoiceData?.client_tax_registration_number && (
                <p>
                  {t("tax_number")}{" "}
                  {invoiceData?.client_tax_registration_number}
                </p>
              )}
              {/* If backend has client mobile/tax add here */}
            </div>
          </div>

          {/* TABLE */}
          <Table>
            <thead>
              <tr>
                <th>{t("No.")}</th>
                <th>{t("subtotal")}</th>
                <th>{t("total")}</th>
              </tr>
            </thead>
            <tbody>
              {invoiceData?.products?.map((item, i) => (
                <tr key={item.id}>
                  <td>{String(i + 1).padStart(2, "0")}</td>
                  <td>
                    <span className="d-flex align-items-center gap-1">
                      {userData?.organization?.currency?.stringCode || isRTL ? (
                        <RiyalIconSvg width={18} height={18} />
                      ) : (
                        t("sar")
                      )}{" "}
                      {formateAmounts(item?.subtotal)}
                    </span>
                  </td>
                  <td>{formateAmounts(item.total)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </PdfWrapper>
      </Box>

      {/* FOOTER */}
      <Box>
        <Box className="d-flex justify-content-between align-items-end gap-4 totals-section mb-5 no-break">
          <Box
            className="mb-3 d-flex justify-content-start"
            style={{ marginLeft: "0.8rem" }}
          >
            <QRCodeCanvas
              value={`${window.location.origin}/invoice/${invoiceData?.id}`}
              size={80}
              level="H"
            />
          </Box>
          <Totals>
            <p className="totals-content">
              <span className="label">{t("subtotal")}</span>
              <span className="font-bold">
                {userData?.organization?.currency?.stringCode || isRTL ? (
                  <RiyalIconSvg width={18} height={18} />
                ) : (
                  t("sar")
                )}{" "}
                {formateAmounts(invoiceData?.sub_total)}
              </span>
            </p>
            <p className="totals-content">
              <span className="label">{t("discount")}</span>
              <span className="font-bold">
                {userData?.organization?.currency?.stringCode || isRTL ? (
                  <RiyalIconSvg width={18} height={18} />
                ) : (
                  t("sar")
                )}{" "}
                {formateAmounts(invoiceData?.discount)}
              </span>
            </p>
            <p className="totals-content">
              <span className="label">{t("total_after_discount")}</span>
              <span className="font-bold">
                {userData?.organization?.currency?.stringCode || isRTL ? (
                  <RiyalIconSvg width={18} height={18} />
                ) : (
                  t("sar")
                )}{" "}
                {formateAmounts(invoiceData?.amount_after_discount)}
              </span>
            </p>
            <p className="totals-content">
              <span className="label">{t("taxable_amount")}</span>
              <span className="font-bold">
                {userData?.organization?.currency?.stringCode || isRTL ? (
                  <RiyalIconSvg width={18} height={18} />
                ) : (
                  t("sar")
                )}{" "}
                {formateAmounts(invoiceData?.tax_value)}
              </span>
            </p>
            <div className="grand-total">
              <span>{`${t("total")} ${t("inc_tax")}`}</span>
              <span>
                {userData?.organization?.currency?.stringCode || isRTL ? (
                  <RiyalIconSvg stroke="white" width={18} height={18} />
                ) : (
                  t("sar")
                )}{" "}
                {formateAmounts(invoiceData?.total_amount)}
              </span>
            </div>
          </Totals>
        </Box>
        <Footer className="footer no-break">
          <span className="d-flex gap-2 align-items-center">
            <img src={PhoneIconWhite} alt="Phone" width={20} />
            <span
              className="mb-1 p-0"
              style={{ direction: "ltr", unicodeBidi: "embed" }}
            >
              {userData?.organization?.contact_phone || userData?.phone}
            </span>
          </span>
          <span className="d-flex gap-2 align-items-center">
            <img src={Mail} alt="Mail" width={20} />
            <span className="mb-1 p-0">{userData?.email}</span>
          </span>
          {userData?.organization?.address && (
            <span className="d-flex gap-2 align-items-center">
              <img src={Location} alt="Location" width={20} />
              <span className="mb-1 p-0">
                {userData?.organization?.address}
              </span>
            </span>
          )}
        </Footer>
      </Box>
    </PdfPage>
  );
});

export default QRInvoicePDF;
